/**
 * FeatureSuggestionPanel
 *
 * Lightweight panel for submitting feature suggestions via AI chat.
 * Can be triggered from the AgentChatBubble when users express ideas.
 */

import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Lightbulb, X, Send, Plus, Sparkles, Bug, Palette, BookOpen, Check } from 'lucide-react';
import { KnowledgeBaseService } from '../logic/KnowledgeBaseService';
import { SUGGESTION_TYPES } from '../index';

const ICONS = {
  Plus,
  Sparkles,
  Bug,
  Palette,
  BookOpen
};

const FeatureSuggestionPanel = ({
  isOpen,
  onClose,
  user,
  prefillTitle = '',
  prefillDescription = '',
  prefillType = 'new_feature'
}) => {
  const [title, setTitle] = useState(prefillTitle);
  const [description, setDescription] = useState(prefillDescription);
  const [type, setType] = useState(prefillType);
  const [area, setArea] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!title.trim() || !description.trim() || !user?.id) return;

    try {
      setSubmitting(true);
      setError(null);

      await KnowledgeBaseService.submitSuggestion({
        title: title.trim(),
        description: description.trim(),
        type,
        area: area || null,
        userId: user.id,
        userEmail: user.email
      });

      setSubmitted(true);
      setTimeout(() => {
        onClose();
        // Reset form
        setTitle('');
        setDescription('');
        setType('new_feature');
        setArea('');
        setSubmitted(false);
      }, 2000);
    } catch (err) {
      console.error('[FeatureSuggestionPanel] Submit error:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          className="absolute bottom-full right-0 mb-2 w-80 bg-gray-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-white/10 bg-gradient-to-r from-[#AEA36D]/10 to-transparent">
            <div className="flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-[#AEA36D]" />
              <span className="text-sm font-semibold text-white">Submit Suggestion</span>
            </div>
            <button
              onClick={onClose}
              className="text-white/40 hover:text-white transition"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-6 text-center"
            >
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3">
                <Check className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-white font-medium mb-1">Suggestion Submitted!</h3>
              <p className="text-sm text-white/60">Thanks for your feedback</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="p-3 space-y-3">
              {error && (
                <div className="p-2 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-xs">
                  {error}
                </div>
              )}

              {/* Type selector */}
              <div className="flex flex-wrap gap-1">
                {Object.entries(SUGGESTION_TYPES).map(([key, val]) => {
                  const Icon = ICONS[val.icon] || Lightbulb;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setType(val.id)}
                      className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs transition ${
                        type === val.id
                          ? 'bg-[#AEA36D] text-black'
                          : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      {val.name}
                    </button>
                  );
                })}
              </div>

              {/* Title */}
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="What's your idea?"
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/40 focus:outline-none focus:border-[#AEA36D]/50"
                required
                autoFocus
              />

              {/* Description */}
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe it briefly..."
                rows={3}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/40 focus:outline-none focus:border-[#AEA36D]/50 resize-none"
                required
              />

              {/* Area dropdown */}
              <select
                value={area}
                onChange={(e) => setArea(e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#AEA36D]/50"
              >
                <option value="">Select area (optional)</option>
                <option value="assignments">Assignments</option>
                <option value="mobile_app">Mobile App</option>
                <option value="client_portal">Client Portal</option>
                <option value="finance">Finance</option>
                <option value="equipment">Equipment</option>
                <option value="ai_agents">AI Agents</option>
                <option value="other">Other</option>
              </select>

              {/* Submit button */}
              <button
                type="submit"
                disabled={submitting || !title.trim() || !description.trim()}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#AEA36D] text-black rounded-lg font-medium text-sm hover:bg-[#C4B76D] transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Submit Suggestion
                  </>
                )}
              </button>
            </form>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FeatureSuggestionPanel;
